import { BALANCE } from '../balance.ts';
import { PUZZLES } from '../puzzles/registry.ts';
import { Rng } from '../rng.ts';
import {
  NOTICE_FOR_SABOTAGE,
  type ActionError,
  type ExecState,
  type NoticeKind,
  type PlayerId,
  type Role,
  type SabotageKind,
  type ScheduledEvent,
} from '../types.ts';
import { emit, fail, idsWithRole, roleOf, type Ctx } from './context.ts';
import { activePuzzles, addAlarm } from './core.ts';

/**
 * The infiltrator's three sabotages and the random events that imitate them (GDD §4).
 * Both go through the same code and produce the same public notice, so nothing the thieves
 * can see tells them apart. The cause is written to the log's `secret` for the Reveal.
 */

const NOTICES: readonly NoticeKind[] = ['suspiciousGuard', 'interference', 'patrolEarly'];

/** Plans the 2–4 random events and the driver's radio bulletins for this heist. */
export function scheduleEvents(ctx: Ctx): void {
  const s = ctx.s;
  const exec = s.exec as ExecState;
  const rng = Rng.create(s.seed).fork('events');
  const count = rng.int(BALANCE.randomEventsMin, BALANCE.randomEventsMax);
  const lo = BALANCE.noSabotageFirstMs + 5_000;
  const hi = Math.max(lo + 1, Math.floor((exec.timerUs / 1000) * 0.85));
  const times: number[] = [];
  for (let i = 0; i < count; i++) {
    for (let tries = 0; tries < 60; tries++) {
      const t = rng.int(lo, hi);
      if (times.every((o) => Math.abs(o - t) >= BALANCE.minRandomEventSpacingMs)) {
        times.push(t);
        break;
      }
    }
  }
  times.sort((a, b) => a - b);
  exec.scheduled = times.map((atMs): ScheduledEvent => {
    const kind = rng.pick(NOTICES);
    const announced = rng.chance(BALANCE.bulletinChancePermille);
    const lead = rng.int(BALANCE.bulletinLeadMinMs, BALANCE.bulletinLeadMaxMs);
    return {
      atMs,
      kind,
      fired: false,
      bulletinAtMs: announced ? Math.max(1_000, atMs - lead) : null,
    };
  });
}

/** Picks something to alter among the unsolved puzzles the team is working on. */
function tamperOne(
  ctx: Ctx,
  exclude: Role | null,
  by: PlayerId | 'event',
): { puzzleId: string; clueId: string; original: unknown; shown: unknown } | null {
  const s = ctx.s;
  const exec = s.exec as ExecState;
  const rng = Rng.create(`${s.seed}/tamper/${s.nextEventId}`);
  const candidates = rng.shuffle(activePuzzles(s).filter((p) => !p.solved));
  for (const p of candidates) {
    const choice = PUZZLES[p.type].tamper(p, rng, exclude);
    if (!choice) continue;
    const clue = p.clues.find((c) => c.id === choice.clueId);
    if (!clue) continue;
    clue.tampered = { value: choice.value, by, atMs: exec.elapsedMs };
    // A short glitch on the terminal of whoever holds the clue (same for events and sabotage).
    emit(ctx, 'clue.glitch', { puzzleId: p.id }, idsWithRole(s, clue.holder));
    return { puzzleId: p.id, clueId: clue.id, original: clue.value, shown: choice.value };
  }
  return null;
}

function notice(ctx: Ctx, kind: NoticeKind, secret: Record<string, unknown>): void {
  const exec = ctx.s.exec as ExecState;
  exec.lastNoticeAtMs = exec.elapsedMs;
  emit(ctx, 'notice', { kind }, 'all', secret);
}

export function useSabotage(ctx: Ctx, by: PlayerId, kind: SabotageKind): ActionError | null {
  const s = ctx.s;
  const exec = s.exec;
  if ((s.phase !== 'execution' && s.phase !== 'escape') || !exec) {
    return fail('wrongPhase', 'sabotage only works during the heist');
  }
  if (s.infiltrator.id !== by) return fail('notAllowed', 'you are not the infiltrator');
  if (!s.infiltrator.available[kind]) return fail('noCharges', 'that sabotage is already used');
  if (exec.elapsedMs < BALANCE.noSabotageFirstMs) return fail('cooldown', 'too early');
  if (exec.elapsedMs - exec.lastNoticeAtMs < BALANCE.minEventGapMs)
    return fail('cooldown', 'too soon after the last event');
  if (exec.pending.length > 0) return fail('cooldown', 'one sabotage at a time');

  const secret: Record<string, unknown> = { cause: 'sabotage', by, kind };
  switch (kind) {
    case 'tipoff':
      exec.pending.push({
        atMs: exec.elapsedMs + BALANCE.tipoffDelayMs,
        kind: 'tipoff',
        cause: 'sabotage',
        by,
      });
      break;
    case 'rush':
      exec.rushUntilMs = exec.elapsedMs + BALANCE.rushMs;
      break;
    case 'tamper': {
      const hit = tamperOne(ctx, roleOf(s, by), by);
      if (!hit) return fail('nothingToDo', 'nothing here can be tampered with');
      Object.assign(secret, hit);
      break;
    }
  }
  s.infiltrator.available[kind] = false;
  notice(ctx, NOTICE_FOR_SABOTAGE[kind], secret);
  return null;
}

/** Called every simulation step during the heist. */
export function processTimed(ctx: Ctx): void {
  const s = ctx.s;
  const exec = s.exec as ExecState;

  // Delayed alarm from "Guardia sospettosa" / Soffiata.
  const due = exec.pending.filter((e) => e.atMs <= exec.elapsedMs);
  if (due.length > 0) {
    exec.pending = exec.pending.filter((e) => e.atMs > exec.elapsedMs);
    for (const e of due) {
      void e;
      addAlarm(ctx, BALANCE.fixed.tipoff, false, 'event');
      if (s.phase === 'reveal') return;
    }
  }

  // Radio bulletins: only the driver hears them, and never for a sabotage.
  for (const ev of exec.scheduled) {
    if (ev.bulletinAtMs !== null && ev.bulletinAtMs <= exec.elapsedMs) {
      const drivers = idsWithRole(s, 'driver');
      if (drivers.length > 0) emit(ctx, 'radio.bulletin', { kind: ev.kind }, drivers);
      ev.bulletinAtMs = null;
    }
  }

  // Random events.
  for (const ev of exec.scheduled) {
    if (ev.fired || ev.atMs > exec.elapsedMs) continue;
    const wait = exec.lastNoticeAtMs + BALANCE.minEventGapMs;
    if (exec.elapsedMs < wait) {
      ev.atMs = wait;
      continue;
    }
    if (ev.kind === 'interference') {
      const hit = tamperOne(ctx, null, 'event');
      if (!hit) {
        ev.atMs = exec.elapsedMs + BALANCE.eventRetryMs;
        continue;
      }
      ev.fired = true;
      notice(ctx, ev.kind, { cause: 'random', kind: 'tamper', ...hit });
    } else if (ev.kind === 'suspiciousGuard') {
      exec.pending.push({
        atMs: exec.elapsedMs + BALANCE.tipoffDelayMs,
        kind: 'tipoff',
        cause: 'random',
        by: null,
      });
      ev.fired = true;
      notice(ctx, ev.kind, { cause: 'random', kind: 'tipoff' });
    } else {
      exec.rushUntilMs = exec.elapsedMs + BALANCE.rushMs;
      ev.fired = true;
      notice(ctx, ev.kind, { cause: 'random', kind: 'rush' });
    }
  }
}
